// Notifications adapter for /api/notifications/*. Only meaningful when
// an org session exists; gracefully returns [] otherwise so the bell
// can render without auth.
//
// The backend wraps the list in { notifications, unread_count } and uses
// snake_case fields like is_read / event_type. We normalize to a flat
// array of camelCase Notification objects for the v2 UI.

import { apiCall, apiSoft } from "./http";

export interface Notification {
  id: string;
  title?: string;
  body?: string;
  message?: string;
  read?: boolean;
  created_at?: string;
  link?: string;
  type?: string;
}

interface RawNotification {
  id: number | string;
  event_type?: string;
  title?: string;
  message?: string;
  body?: string;
  link?: string | null;
  is_read?: boolean;
  read?: boolean;
  created_at?: string | null;
}

interface RawListResponse {
  notifications?: RawNotification[];
  unread_count?: number;
}

function normalize(raw: RawNotification): Notification {
  return {
    id: String(raw.id),
    title: raw.title,
    body: raw.body,
    message: raw.message,
    read: raw.is_read ?? raw.read ?? false,
    created_at: raw.created_at ?? undefined,
    link: raw.link ?? undefined,
    type: raw.event_type,
  };
}

export const notificationsApi = {
  async list(): Promise<Notification[]> {
    const raw = await apiSoft<RawListResponse | RawNotification[]>(
      "/api/notifications",
    );
    if (!raw) return [];
    const arr = Array.isArray(raw) ? raw : (raw.notifications ?? []);
    return arr.map(normalize);
  },
  markRead: (id: string) =>
    apiCall<void>(`/api/notifications/${id}/read`, { method: "POST" }),
  markAllRead: () =>
    apiCall<void>("/api/notifications/read-all", { method: "POST" }),
  clear: () => apiCall<void>("/api/notifications", { method: "DELETE" }),
};
